import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import './ErrorBoundary.css';

// React error boundary. Catches render and lifecycle errors anywhere in the
// subtree it wraps and shows a recoverable fallback instead of letting the
// page go blank. Required as a class component (hooks can't catch).
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console — production would route this to a tracking service.
    console.error('ErrorBoundary caught error:', error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== 'production';
    return (
      <div className="error-boundary">
        <div className="error-boundary-card">
          <AlertTriangle size={40} className="error-boundary-icon" />
          <h2 className="error-boundary-title">Something went wrong</h2>
          <p className="error-boundary-message">
            {this.props.scope ? `The ${this.props.scope} hit an unexpected error.` : 'An unexpected error occurred.'}
            {' '}Try reloading — if it keeps happening, let us know.
          </p>
          <div className="error-boundary-actions">
            <button className="error-boundary-btn-primary" onClick={this.reload}>
              <RotateCcw size={14} />
              Reload page
            </button>
            <button className="error-boundary-btn-secondary" onClick={this.reset}>
              Try again
            </button>
          </div>
          {isDev && this.state.error && (
            <details className="error-boundary-details">
              <summary>Error details (dev only)</summary>
              <pre>{this.state.error.toString()}{'\n'}{this.state.error.stack}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
